import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from "jwt-decode";
import { PublicClientApplication, AuthenticationResult } from "@azure/msal-browser";
import { msalConfig, loginRequest } from "./AzureAuthConfig";

interface AuthContextType {
    isAuthenticated: boolean;
    login: (token: string) => void;
    logout: () => void;
    isAgreementChecked: boolean;
    toggleCheckAgreement: () => void;
    azureLogin: () => void;
}

interface DecodedToken {
    nameid: string;
    exp: number;
    [key: string]: string | number | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const getAuthToken = (): string | null => {
    return localStorage.getItem("authToken");
};

export const getNameID = (): string | null => {
    const authToken = getAuthToken();
    if (authToken) {
        const decoded: DecodedToken = jwtDecode(authToken);
        return decoded.nameid;
    }
    return null;
};

export const getRole = (): string | number | null => {
    const authToken = getAuthToken();
    if (authToken) {
        const decoded: DecodedToken = jwtDecode(authToken);
        return decoded.role;
    }
    return null;
};

export const isTokenExpired = (): boolean => {
    const authToken = getAuthToken();
    if (authToken) {
        const decoded: DecodedToken = jwtDecode(authToken);
        return decoded.exp < Date.now() / 1000;
    }
    return true;
};

const msalInstance = new PublicClientApplication(msalConfig);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(() => {
        const authToken = getAuthToken();
        return authToken !== null && !isTokenExpired();
    });
    const [isAgreementChecked, setIsAgreementChecked] = useState<boolean>(() => {
        const storedValue = localStorage.getItem("isAgreementChecked");
        return storedValue === "true";
    });
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
        if (isAuthenticated && location.pathname !== '/Report') {
            navigate('/ReportSearch');
        }
    }, [isAuthenticated, navigate, location.pathname]);

    const login = (token: string) => {
        setIsAuthenticated(token ? true : false);
        localStorage.setItem("authToken", token);
    };

    const logout = () => {
        setIsAuthenticated(false);
        localStorage.removeItem("authToken");
    };

    const toggleCheckAgreement = () => {
        setIsAgreementChecked(prev => {
            const newValue = !prev;
            localStorage.setItem("isAgreementChecked", newValue.toString());
            return newValue;
        });
    };

    const azureLogin = () => {
        msalInstance.loginPopup(loginRequest)
            .then((response: AuthenticationResult) => {
                const token = response.accessToken;
                // Handle the Azure AD token as needed
                console.log("Azure AD token:", token);
            })
            .catch(error => {
                console.error("Azure AD login error:", error);
            });
    };

    return (
        <AuthContext.Provider value={{ isAuthenticated, login, logout, isAgreementChecked, toggleCheckAgreement, azureLogin }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};